import React, { useState, useEffect, useCallback } from "react"
import { Form, Typography, Row, Col, Spin } from "antd"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useParams, useNavigate } from "react-router-dom"
import { serialize } from "object-to-formdata"
import { routes } from "router/routes"
import { ListingActionButtons } from "layoutsV2/pro/ProNewListingLayout/ListingActionButtons"
import AddStaffModal from "pagesV2/pro/Staff/AddStaffModal"
import { useResourceData } from "./hooks/useResourceData"
import { useEquipmentManagement } from "./hooks/useEquipmentManagement"
import { useCrewMemberManagement } from "./hooks/useCrewMemberManagement"
import { EquipmentSection } from "./components/EquipmentSection"
import { CrewMemberSection } from "./components/CrewMemberSection"
import { updateTrip, getGuideTrip } from "store/trips"
import { setFormTouched, setFormValues } from "store/proNewListingForms"
import { modelSelector } from "store/selectors"

const { Title, Text } = Typography

export const NewListingResources = () => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const [isLoading, setIsLoading] = useState(false)
  const [autoAssignEquipment, setAutoAssignEquipment] = useState(false)
  const trip = useSelector(modelSelector("trip"))

  // Custom hooks for data and state management
  const { equipmentData, crewMemberData, categoryOptions, roleOptions } = useResourceData()

  const {
    selectedEquipment,
    setSelectedEquipment,
    selectedCategories,
    setSelectedCategories,
    selectedEquipmentData,
    handleEditEquipment,
    handleRemoveEquipment
  } = useEquipmentManagement(equipmentData)

  const {
    selectedCrewMembers,
    setSelectedCrewMembers,
    selectedCrewMembersData,
    selectedRoles,
    setSelectedRoles,
    isAddStaffModalVisible,
    editingCrewMember,
    handleEditCrewMember,
    handleRemoveCrewMember,
    handleOpenStaffModal,
    handleCloseStaffModal
  } = useCrewMemberManagement(crewMemberData)

  const handleRemoveEquipmentWrapper = useCallback((id) => {
    handleRemoveEquipment(id)
    dispatch(setFormTouched("resources", true))
  }, [handleRemoveEquipment, dispatch])

  const handleRemoveCrewMemberWrapper = useCallback((id) => {
    handleRemoveCrewMember(id)
    dispatch(setFormTouched("resources", true))
  }, [handleRemoveCrewMember, dispatch])

  // Fetch trip data on mount
  useEffect(() => {
    if (id) {
      dispatch(getGuideTrip(id))
    }
  }, [dispatch, id])

  // Initialize selected equipment and crew from trip data.
  // Guard with trip.loaded: when a save is in-flight, Redux sets loaded=false
  // but trip data is still stale. Without the guard, the effect re-runs and
  // resets local state to the old values, causing a visible flicker.
  useEffect(() => {
    if (!trip?.loaded) return
    if (trip.boat_ids) {
      setSelectedEquipment(trip.boat_ids.map(String))
    }
    if (trip.assigned_staff_ids) {
      setSelectedCrewMembers(trip.assigned_staff_ids.map(String))
    }
    if (trip.auto_assign_equipment !== undefined) {
      setAutoAssignEquipment(trip.auto_assign_equipment)
    }
  }, [trip, setSelectedEquipment, setSelectedCrewMembers])

  const handleValuesChange = (_changedValues, allValues) => {
    dispatch(setFormValues("resources", allValues))
    dispatch(setFormTouched("resources", true))
  }

  // Wrap selection handlers to mark form as touched when user changes selections
  const handleEquipmentChange = useCallback((equipment) => {
    setSelectedEquipment(equipment)
    dispatch(setFormTouched("resources", true))
  }, [setSelectedEquipment, dispatch])

  const handleCrewMemberChange = useCallback((crew) => {
    setSelectedCrewMembers(crew)
    dispatch(setFormTouched("resources", true))
  }, [setSelectedCrewMembers, dispatch])

  const handleAutoAssignChange = useCallback((value) => {
    setAutoAssignEquipment(value)
    dispatch(setFormTouched("resources", true))
  }, [dispatch])

  const onFinish = useCallback((callback, onError) => {
    setIsLoading(true)
    const formData = serialize(
      {
        trip: {
          boat_ids: selectedEquipment.filter((id) => id !== "all").map(Number),
          assigned_staff_ids: selectedCrewMembers.filter((id) => id !== "all").map(Number),
          auto_assign_equipment: autoAssignEquipment
        }
      },
      { allowEmptyArrays: true }
    )

    dispatch(updateTrip(id, formData))
      .then(() => {
        dispatch(setFormTouched("resources", false))
        setIsLoading(false)
        callback?.()
      })
      .catch(() => {
        setIsLoading(false)
        onError?.()
      })
  }, [selectedEquipment, selectedCrewMembers, autoAssignEquipment, dispatch, id])

  // Adding equipment leaves the wizard for the equipment-add page. Save the
  // touched resources step first so the wizard's unsaved-changes guard doesn't
  // prompt, then navigate once the save resolves.
  const handleAddEquipment = useCallback(() => {
    onFinish(() => navigate(routes.guideEquipmentAddPath()))
  }, [onFinish, navigate])

  // Listen for auto-save requests when user switches tabs
  useEffect(() => {
    const handleResourcesSave = (event) => {
      onFinish(
        () => event.detail.resolve(),
        () => event.detail.reject(new Error("resources_save_failed"))
      )
    }
    window.addEventListener("resourcesSaveRequest", handleResourcesSave)
    return () => window.removeEventListener("resourcesSaveRequest", handleResourcesSave)
  }, [onFinish])

  return (
    <Spin spinning={isLoading} size="large" style={{ width: "100%" }}>
      <Form form={form} className="d-flex flex-column flex-grow-1" onValuesChange={handleValuesChange}>
        <Title level={3} className="mb-0">
          {t("listings.tabs.resources.title")}
        </Title>
        <Text type="primary" className="text-lg">
          {t("listings.tabs.resources.new_subtitle")}
        </Text>

        <Row gutter={[16, 16]} className="mt-4">
          <Col xl={12} span={24}>
            <EquipmentSection
              equipmentData={equipmentData}
              selectedEquipment={selectedEquipment}
              onEquipmentChange={handleEquipmentChange}
              selectedCategories={selectedCategories}
              onCategoryChange={setSelectedCategories}
              categoryOptions={categoryOptions}
              selectedEquipmentData={selectedEquipmentData}
              onEdit={handleEditEquipment}
              onRemove={handleRemoveEquipmentWrapper}
              onAddClick={handleAddEquipment}
              autoAssignEquipment={autoAssignEquipment}
              onAutoAssignChange={handleAutoAssignChange}
            />
          </Col>

          <Col xl={12} span={24}>
            <CrewMemberSection
              crewMemberData={crewMemberData}
              selectedCrewMembers={selectedCrewMembers}
              onCrewMemberChange={handleCrewMemberChange}
              selectedRoles={selectedRoles}
              onRoleChange={setSelectedRoles}
              roleOptions={roleOptions}
              selectedCrewMembersData={selectedCrewMembersData}
              onEdit={handleEditCrewMember}
              onRemove={handleRemoveCrewMemberWrapper}
              onAddClick={handleOpenStaffModal}
            />
          </Col>
        </Row>

        <ListingActionButtons form={form} onFinish={onFinish} />

        <AddStaffModal staffId={editingCrewMember?.id} editMode={!!editingCrewMember?.id} open={isAddStaffModalVisible} onCancel={handleCloseStaffModal} memberType="crew" />
      </Form>
    </Spin>
  )
}
